import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { sprintf } from 'sprintf-js';

import { environment } from '../../environments/environment';
import { BaseService } from './base.service';

@Injectable()
export class EmailTemplateService extends BaseService {

  constructor(
    protected http: HttpClient,
  ) {
    super(http, `${environment.apiBaseUrl}/companies/%(company_id)s/email_templates`);
  }

  copyTemplates(payload: any, urlParams: any = {}): Observable<any> {
    const url = sprintf(`${environment.apiBaseUrl}/companies/%(company_id)s/email_templates_copy`, urlParams);
    return this.http.post<any>(url, payload);
  }

  private attachmentsUrl(emailTemplateId: string, urlParams: any): string {
    return `${sprintf(this.baseUrl, urlParams)}/${emailTemplateId}/attachments`;
  }

  listAttachments(emailTemplateId: string, urlParams: any = {}): Observable<any> {
    return this.http.get<any>(this.attachmentsUrl(emailTemplateId, urlParams));
  }

  // Direct-to-S3 upload. The component still calls this method like any other —
  // signature unchanged — but internally we no longer stream the file through
  // the API. Phone JPGs are 5–25 MB; running them through the multer route
  // gets killed by the proxy in front of the Node process (ALB idle_timeout /
  // CloudFront body cap). Flow:
  //   1. POST /attachments/s3_put_url → backend returns a presigned PUT URL
  //      with ONLY Content-Type signed (no ContentDisposition / ACL) so the
  //      browser's auto-set header is enough to satisfy AWS SigV4.
  //   2. PUT the raw file to S3 with that explicit Content-Type header.
  //   3. PUT /attachments/s3_put_url → backend persists the EmailTemplateAttachment
  //      row and returns it for the UI list.
  uploadAttachment(emailTemplateId: string, file: File, urlParams: any = {}): Observable<any> {
    const contentType = file.type || 'application/octet-stream';
    const presignUrl = `${this.attachmentsUrl(emailTemplateId, urlParams)}/s3_put_url`;
    return this.http.post<any>(presignUrl, {
      file: { name: file.name, type: contentType },
    }).pipe(
      switchMap((s3File: any) => {
        // The Auth interceptor skips amazonaws.com URLs, so no Authorization
        // header pollutes this request and invalidates the SigV4 signature.
        const headers = new HttpHeaders({ 'Content-Type': contentType });
        return this.http.put(s3File.url, file, { headers, responseType: 'text' }).pipe(
          switchMap(() => this.http.put<any>(presignUrl, {
            file: { key: s3File.key, name: file.name, type: contentType },
          })),
        );
      }),
    );
  }

  linkDownloadForms(emailTemplateId: string, downloadFormIds: number[], urlParams: any = {}): Observable<any> {
    return this.http.put<any>(
      `${this.attachmentsUrl(emailTemplateId, urlParams)}/link`,
      { download_form_ids: downloadFormIds },
    );
  }

  deleteAttachment(emailTemplateId: string, attachmentId: number, urlParams: any = {}): Observable<any> {
    return this.http.delete<any>(`${this.attachmentsUrl(emailTemplateId, urlParams)}/${attachmentId}`);
  }
}
